import React from 'react';
import { useTheme } from 'styled-components';
import Image from 'next/image';
import { v4 as uuidv4 } from 'uuid';

import { StrategyComposerProps } from 'Common/util/cmsComposer/Strategy';

import Main from 'Elements/Main';
import { H2 } from 'Elements/Typography';
import Pill from 'Elements/Pill';

import Grid, { Col } from 'Components/Grid';
import Video from 'Components/Video/Video';
import Table from 'Components/Table';

import StrategyTabNavigation from 'Modules/TabNavigation/Strategy';
import StrategyNavigation, { StratNavProps } from 'Modules/SectionNavigation/Strategy';

import WaveSVG from 'public/graphics/waves/strategy/strategy-wave-104.svg';

import {
    Background,
    ImageWrapper,
    Heading,
    ContentContainer,
    Wrapper,
    InfoWrapper,
    ContentWrapper,
    Container,
    ContentImageWrapper,
    KeyPointContainer,
    PageNumber,
    Pagination,
    PaginationWrapper
} from './Content.styled';

import QuoteBlock from 'Components/QuoteBlock/QuoteBlock';

import { Tab } from 'Modules/TabNavigation/Content/Large/Large';
import { PaginationProps } from 'Components/Pagination';

interface StrategyTabs extends Tab {
    content: StrategyComposerProps[];
}

export interface Props {
    heading?: string;
    content: StrategyComposerProps[];
    pageNumber: number;
    backgroundColor?: string;
    navigation: StratNavProps;
    tabNavigation?: { tabs: StrategyTabs[] };
    pagination: PaginationProps;
}

const Content: React.FC<Props> = props => {
    const { colors } = useTheme();
    const { heading, backgroundColor, navigation, pageNumber, content, tabNavigation, pagination } =
        props;

    const composerMap = composedData =>
        composedData.map((item, index) => (
            <React.Fragment key={`stratComposerItem-${index}`}>
                {item.videoComponent && (
                    <Wrapper>
                        <Grid>
                            <Col>
                                <Video {...item.videoComponent.embed} />
                            </Col>
                        </Grid>
                    </Wrapper>
                )}
                {item.textBlock && (
                    <Wrapper>
                        <Grid>
                            <Col>{item.textBlock.text}</Col>
                        </Grid>
                    </Wrapper>
                )}
                {item.blockquote && (
                    <Wrapper>
                        <QuoteBlock
                            {...item.blockquote}
                            backgroundColor={colors.secondary.tints[1].hex}
                            quoteColorLeft={colors.primaryContrast.hex}
                            quoteColorRight={colors.primaryContrast.hex}
                            textColor={colors.text.hex}
                        />
                    </Wrapper>
                )}
                {item.image && (
                    <ContentImageWrapper>
                        <Image {...item.image} fill style={{ objectFit: 'cover' }} />
                    </ContentImageWrapper>
                )}
                {item.table && (
                    <Wrapper>
                        <Table {...item.table} />
                    </Wrapper>
                )}
                {item.strategySummaryKeyPoints && (
                    <Wrapper>
                        {item.strategySummaryKeyPoints.map((keypoint, index) => (
                            <Wrapper key={`wrapper-${index}`}>
                                {keypoint.heading && <H2>{keypoint.heading}</H2>}
                                <KeyPointContainer>
                                    {keypoint.summaryList.map(listItem => (
                                        <Pill key={`listPill-${uuidv4()}`}>{listItem}</Pill>
                                    ))}
                                </KeyPointContainer>
                            </Wrapper>
                        ))}
                    </Wrapper>
                )}
            </React.Fragment>
        ));

    return (
        <Main>
            <Container>
                <Background
                    style={
                        {
                            '--bg-color': backgroundColor
                        } as React.CSSProperties
                    }
                >
                    <ImageWrapper>
                        <Image src={WaveSVG} alt="" fill style={{ objectFit: 'cover' }} />
                    </ImageWrapper>
                </Background>
                <ContentWrapper>
                    <Grid>
                        <Col l={3}>
                            <StrategyNavigation {...navigation} />
                        </Col>
                        <Col l={9}>
                            <InfoWrapper>
                                <Pill backgroundColor={colors.primaryContrast.hex}>
                                    <Heading>SOLENT STRATEGY 2035</Heading>
                                </Pill>
                                <Pill backgroundColor={colors.primaryContrast.hex}>
                                    <PageNumber>{`${pageNumber}/${navigation.links.length}`}</PageNumber>
                                </Pill>
                            </InfoWrapper>
                            <ContentContainer
                                $tabNavigation={tabNavigation && tabNavigation.tabs.length >= 1}
                            >
                                {heading && <H2>{heading}</H2>}
                                {composerMap(content)}
                            </ContentContainer>
                            {tabNavigation && tabNavigation.tabs.length >= 1 && (
                                <StrategyTabNavigation
                                    tabs={tabNavigation.tabs.map(tab => ({
                                        label: tab.label,
                                        ariaLabel: tab.ariaLabel,
                                        id: tab.id,
                                        content: composerMap(tab.content)
                                    }))}
                                />
                            )}
                            <PaginationWrapper>
                                <Pagination {...pagination} />
                            </PaginationWrapper>
                        </Col>
                    </Grid>
                </ContentWrapper>
            </Container>
        </Main>
    );
};

export default Content;
